import { z } from 'zod';
import { assignmentSchema } from './issuance.js';
import { conditionSchema } from './catalog.js';
/**
 * The lifecycle after signing (REQUIREMENTS §2.5: Return, Consumables,
 * Report lost or damaged). Every endpoint here closes or flags one
 * assignment inside one transaction with its ledger entry.
 */
/**
 * POST /api/assignments/:id/return — the soldier hands it back and a
 * csm or commander confirms. The condition is mandatory (§2.5):
 * it decides whether the item goes to stock, to repair, or out of service.
 * §3.3 answered: no third party, for controlled items either.
 */
export const returnRequestSchema = z.object({
    condition: conditionSchema,
    notes: z.string().trim().max(500).nullable().default(null),
});
/**
 * POST /api/assignments/:id/expend — consumables are never returned. A
 * training day ends with the rounds gone, not lost (§2.5 "Consumables").
 */
export const expendRequestSchema = z.object({
    /** Defaults to everything on the assignment. */
    quantity: z.number().int().min(1).optional(),
    notes: z.string().trim().max(500).nullable().default(null),
});
/**
 * POST /api/assignments/:id/report — lost or damaged, filed by the soldier
 * or a commander, on non-consumables only. The photo is optional and never
 * mandatory: a required photo would slow the storeroom line on the
 * ninety-nine handovers where nothing is in dispute (§2.5).
 */
export const MAX_EVIDENCE_BYTES = 2 * 1024 * 1024;
export const reportRequestSchema = z.object({
    kind: z.enum(['lost', 'damaged']),
    notes: z.string().trim().min(3).max(1000),
    /** A JPEG or PNG data URL, at most MAX_EVIDENCE_BYTES decoded. */
    photoDataUrl: z
        .string()
        .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/)
        .nullable()
        .default(null),
});
/**
 * POST /api/assignments/:id/found — a commander closes a report the good
 * way: the item turned up. Back to active, the unit back to issued.
 */
export const foundRequestSchema = z.object({
    notes: z.string().trim().max(500).nullable().default(null),
});
/**
 * POST /api/assignments/:id/write-off — the item leaves the books. Level 3
 * only. Closes a lost/damaged report the hard
 * way, or writes off a consumable/damaged item that will not come back.
 */
export const writeOffRequestSchema = z.object({
    reason: z.string().trim().min(3).max(1000),
});
export const assignmentResponseSchema = z.object({ assignment: assignmentSchema });
/** GET /api/reports — open loss/damage reports in the caller's reach (§2.6 alerts). */
export const reportsResponseSchema = z.object({
    assignments: z.array(assignmentSchema),
    /** Names for the soldiers that appear above, so the list reads without a lookup per row. */
    soldiers: z.array(z.object({ uid: z.string(), name: z.string(), unitName: z.string().nullable() })),
});
//# sourceMappingURL=lifecycle.js.map